import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { UserButton } from "@clerk/react";
import {
  LayoutDashboard, FileText, Truck, CheckSquare, Building2, Package, FolderOpen, PencilRuler, Users, ScrollText, Menu, Boxes, Cpu, Upload, KeyRound, Settings2, UserCog, Settings, ChevronDown, FileSignature, UserPlus, ListChecks, BarChart3, CalendarRange, Warehouse, ClipboardCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { GlobalSearch } from "@/components/app/GlobalSearch";
import { CreateButtons } from "@/components/order/CreateButtons";
import { useMe } from "@/components/app/MeContext";

const GROUPS = [
  { title: "Operations", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/orders", label: "LM Register", icon: FileText },
    { to: "/approvals", label: "Approvals", icon: CheckSquare },
    { to: "/invoices", label: "Invoice Register", icon: ScrollText },
    { to: "/dispatch-planning", label: "Dispatch Plan", icon: CalendarRange, perm: "planning.view" },
    { to: "/quality-control", label: "Quality Control", icon: ClipboardCheck, perm: "qc.view" },
    { to: "/logistics", label: "Dispatch & Logistics", icon: Truck },
    { to: "/reports", label: "Reports", icon: BarChart3, perm: "reports.view" },
  ] },
  { title: "Sales Hub", items: [
    { to: "/sales/quotes", label: "Quotations", icon: FileSignature, perm: "sales.view" },
    { to: "/sales/prospects", label: "Prospects", icon: UserPlus, perm: "sales.view" },
    { to: "/sales/terms", label: "Terms & Conditions", icon: ListChecks, perm: "sales.view" },
    { to: "/sales/team", label: "Sales Team", icon: UserCog, perm: "admin.settings" },
    { to: "/sales/letterhead", label: "Quotation settings", icon: Settings2, perm: "admin.settings" },
  ] },
  { title: "Masters", items: [
    { to: "/masters/customers", label: "Customers Master", icon: Building2 },
    { to: "/masters/items", label: "Item Master", icon: Package },
    { to: "/masters/logistics", label: "Warehouse & Transport Masters", icon: Warehouse },
  ] },
  { title: "Records", items: [{ to: "/documents", label: "Documents", icon: FolderOpen }] },
  { title: "Installed base", items: [
    { to: "/assets/groups", label: "Asset Group Register", icon: Boxes },
    { to: "/assets/units", label: "Asset Unit Register", icon: Cpu },
    { to: "/assets/import", label: "Asset Upload", icon: Upload, perm: "assets.import" },
  ] },
  { title: "Engineering", items: [{ to: "/designs", label: "Design", icon: PencilRuler, tag: "stub" }] },
  { title: "Admin", admin: true, items: [
    { to: "/admin/users", label: "Users", icon: Users, perm: "admin.users" },
    { to: "/admin/roles", label: "Roles & Permissions", icon: KeyRound, perm: "admin.roles" },
    { to: "/admin/config", label: "Configuration masters", icon: Settings2, perm: "admin.settings" },
    { to: "/admin/audit", label: "Audit & utilities", icon: ScrollText, perm: "admin.audit" },
  ] },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const FOLD_KEY = "tessol.nav.folded";

function NavGroup({ g, folded, toggle, onNavigate }) {
  const open = !folded;
  return (
    <div data-testid={`nav-group-${slug(g.title)}`}>
      <button onClick={toggle} className="w-full flex items-center justify-between px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold hover:text-slate-300 transition-colors"
        aria-expanded={open} data-testid={`nav-group-toggle-${slug(g.title)}`}>
        {g.title}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="flex flex-col gap-0.5">
          {g.items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate} data-testid={`nav-${slug(it.label)}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors ${isActive
                ? "bg-slate-800 text-sky-400 font-medium" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"}`}>
              <it.icon className="h-4 w-4" />
              <span className="flex-1">{it.label}</span>
              {it.tag && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-800 text-amber-400">{it.tag}</span>}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function Nav({ onNavigate }) {
  const { me, can } = useMe();
  const isAdmin = me.roles.includes("admin");
  const [folded, setFolded] = useState(() => JSON.parse(sessionStorage.getItem(FOLD_KEY) || "[]"));
  const toggle = (t) => {
    const next = folded.includes(t) ? folded.filter((x) => x !== t) : [...folded, t];
    setFolded(next);
    sessionStorage.setItem(FOLD_KEY, JSON.stringify(next));
  };
  const groups = GROUPS.filter((g) => !g.admin || isAdmin).map((g) => ({ ...g, items: g.items.filter((it) => (!it.perm || can(it.perm)) && (!it.any || it.any.some(can))) })).filter((g) => g.items.length);
  return (
    <nav className="flex flex-col gap-5 px-3 py-5">
      {groups.map((g) => <NavGroup key={g.title} g={g} folded={folded.includes(g.title)} toggle={() => toggle(g.title)} onNavigate={onNavigate} />)}
    </nav>
  );
}

const Brand = () => (
  <div className="h-14 flex items-center gap-2 px-6 border-b border-slate-800">
    <img src="/tessol-logo.jpg" alt="Tessol" className="h-8 w-8 rounded bg-white object-contain p-0.5" data-testid="brand-logo" />
    <div className="leading-tight">
      <div className="text-slate-100 font-semibold font-display text-sm">Tessol Platform</div>
      <div className="text-[10px] text-slate-500 font-mono">Module 1 · Orders</div>
    </div>
  </div>
);

export default function Layout() {
  const { me, can } = useMe();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden lg:flex flex-col w-[250px] bg-slate-900 fixed inset-y-0 left-0" data-testid="sidebar">
        <Brand />
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="sidebar-scroll"><Nav /></div>
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[260px] p-0 bg-slate-900 border-slate-800 flex flex-col">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Brand />
          <div className="flex-1 min-h-0 overflow-y-auto" data-testid="mobile-sidebar-scroll"><Nav onNavigate={() => setOpen(false)} /></div>
        </SheetContent>
      </Sheet>
      <div className="flex-1 lg:ml-[250px] min-w-0">
        <header className="h-14 sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center gap-3 px-4 lg:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </Button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2"><CreateButtons /></span>
            {me.roles.includes("admin") && (
              <Button variant="ghost" size="icon" title="Admin" aria-label="Open Admin" onClick={() => navigate("/admin/users")} data-testid="topbar-admin-btn">
                <Settings className="h-5 w-5 text-slate-600" />
              </Button>
            )}
            <span className="hidden md:inline text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-slate-100 text-slate-600" data-testid="topbar-role-badge">
              {me.roles.join(" + ")}
            </span>
            <UserButton />
          </div>
        </header>
        <main className="p-4 lg:p-6 max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
